import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Paper,
  Stack,
  LinearProgress,
  Tooltip,
  Alert,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import socketIOClient from "socket.io-client";
import { api } from "../../mockData";

import {
  startSipTrace,
  stopSipTrace,
  getSipTraceStatus,
  getSipTraceMessages,
  getSipTraceErrors,
  sipTraceNewMessage,
  sipTraceBatchMessages,
  sipTraceStatsUpdate,
  sipTraceCaptureStopped,
  clearSipTrace,
  getSipTraceMessageDetail,
  getSipTraceCallMessages,
} from "../../redux/actions/sipTraceAction";

import SipTraceMessageGrid from "./SipTraceMessageGrid";
import SipTraceLadderDiagram from "./SipTraceLadderDiagram";
import SipTraceErrorDashboard from "./SipTraceErrorDashboard";

const MAX_CAPTURE_SECONDS_DEFAULT = 300;
const drawerWidth = 240;

const SipTracer = ({ colorThem }) => {
  const isDark = colorThem === "theme_white";

  // Dialogs render in a React portal outside .App, so the scoped CSS rules in
  // style.css (".App.theme_white .sip-tracer-page ...") never match descendants
  // inside a Dialog. We replicate the needed color tokens here via nested sx so
  // every text/input/icon inside every SIP Tracer dialog stays legible.
  // Direct sx for dialog action buttons (Cancel/Close/Copy Raw/Start Capture).
  // Applied per-button because MUI's `color="primary"` emotion styles sometimes
  // win over nested PaperProps sx selectors due to specificity quirks — this
  // guarantees near-white text on the dark Paper.
  const dialogButtonSx = isDark
    ? {
        color: "#f5f5f5",
        fontWeight: 600,
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
        "& .MuiSvgIcon-root": { color: "#f5f5f5" },
      }
    : {};

  const dialogPaperSx = isDark
    ? {
        backgroundColor: "#1a2332",
        color: "#e0e0e0",
        backgroundImage: "none",
        "& .MuiDialogContent-dividers": {
          borderTopColor: "#2a3a4a",
          borderBottomColor: "#2a3a4a",
        },
        "& .MuiDivider-root": { borderColor: "#2a3a4a" },
        "& .MuiTypography-root": { color: "#e0e0e0" },
        "& .MuiInputLabel-root": { color: "#8899aa" },
        "& .MuiInputLabel-root.Mui-focused": { color: "#42a5f5" },
        "& .MuiInputBase-input": { color: "#e0e0e0" },
        "& .MuiInputBase-input::placeholder": { color: "#6a7a8a", opacity: 1 },
        "& .MuiSelect-select": { color: "#e0e0e0" },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#3a4a5a" },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#4a5a6a",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#42a5f5",
        },
        "& .MuiSelect-icon": { color: "#8899aa" },
        "& .MuiIconButton-root": { color: "#a0b4c8" },
        "& .MuiIconButton-root .MuiSvgIcon-root": { color: "#a0b4c8" },
        "& .MuiToggleButton-root": {
          color: "#a0b4c8",
          borderColor: "#3a4a5a",
        },
        "& .MuiToggleButton-root.Mui-selected": {
          backgroundColor: "#253a52",
          color: "#fff",
        },
        "& .MuiToggleButton-root.Mui-selected:hover": {
          backgroundColor: "#2c4562",
        },
        "& .MuiChip-outlined": {
          color: "#c0c8d0",
          borderColor: "#3a4a5a",
        },
        "& .MuiDialogActions-root .MuiButtonBase-root.MuiButton-root": {
          color: "#f5f5f5 !important",
          fontWeight: 600,
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
        },
        "& .MuiDialogActions-root .MuiButtonBase-root.MuiButton-root .MuiSvgIcon-root": {
          color: "#f5f5f5 !important",
        },
        "& code": {
          backgroundColor: "#0f1a24",
          color: "#ffb74d",
          padding: "1px 5px",
          borderRadius: "3px",
          fontSize: "0.85em",
        },
        "& b": { color: "#fff" },
      }
    : {
        // Light-mode dialog: MUI defaults are mostly fine, but give <code>
        // a subtle background so snippets are readable, and ensure Typography
        // isn't accidentally hit by global !important rules leaking through.
        "& code": {
          backgroundColor: "#f5f5f5",
          color: "#c7254e",
          padding: "1px 5px",
          borderRadius: "3px",
          fontSize: "0.85em",
        },
      };
  const dispatch = useDispatch();
  const {
    capturing,
    actionLoading,
    messages,
    callMessages,
    errors,
    stats,
    session,
    selectedMessage,
    stopReason,
  } = useSelector((state) => state.sipTrace);

  const [activeTab, setActiveTab] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [captureMode, setCaptureMode] = useState("quick"); // "quick" or "filtered"
  const [captureDuration, setCaptureDuration] = useState(300);
  const [captureFilters, setCaptureFilters] = useState({
    did: "",
    caller_id: "",
    carrier: "",
    remote_ip: "",
  });
  const socketRef = useRef(null);
  const timerRef = useRef(null);

  // --- Fetch initial status on mount ---
  useEffect(() => {
    dispatch(getSipTraceStatus());
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [dispatch]);

  // --- SocketIO connection ---
  useEffect(() => {
    const socket = socketIOClient(`${api.dev}`, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("subscribe_sip_trace");
    });

    socket.on("sip_trace_event", (data) => {
      if (!data || !data.type) return;

      switch (data.type) {
        case "new_message":
          if (data.data) {
            dispatch(sipTraceNewMessage(data.data));
          }
          break;
        case "new_messages_batch":
          if (data.data && Array.isArray(data.data)) {
            dispatch(sipTraceBatchMessages(data.data));
          }
          break;
        case "stats_update":
          if (data.data) {
            dispatch(sipTraceStatsUpdate(data.data));
          }
          break;
        case "capture_stopped":
          dispatch(sipTraceCaptureStopped(data.reason || "auto"));
          toast.info(`SIP Tracer stopped: ${data.reason || "auto"}`);
          break;
        case "status_update":
          // Initial status on subscribe
          break;
        default:
          break;
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [dispatch]);

  // --- Elapsed timer during capture ---
  useEffect(() => {
    if (capturing) {
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [capturing]);

  // --- When capture stops, fetch full dataset ---
  useEffect(() => {
    if (stopReason) {
      dispatch(getSipTraceMessages());
      dispatch(getSipTraceErrors());
    }
  }, [stopReason, dispatch]);

  // --- Handlers ---
  const handleStart = useCallback(() => {
    setStartDialogOpen(true);
  }, []);

  const handleStartConfirm = useCallback(() => {
    setStartDialogOpen(false);
    dispatch(clearSipTrace());
    // Build request payload
    const payload = {};
    if (captureMode === "filtered") {
      if (captureFilters.did.trim()) payload.did = captureFilters.did.trim();
      if (captureFilters.caller_id.trim()) payload.caller_id = captureFilters.caller_id.trim();
      if (captureFilters.carrier.trim()) payload.carrier = captureFilters.carrier.trim();
      if (captureFilters.remote_ip.trim()) payload.remote_ip = captureFilters.remote_ip.trim();
      payload.capture_duration = captureDuration;
    } else {
      // Quick capture — no filters, 60 seconds
      payload.capture_duration = 60;
    }
    setCaptureDuration(payload.capture_duration);
    dispatch(startSipTrace(payload));
  }, [dispatch, captureFilters, captureMode, captureDuration]);

  const handleStop = useCallback(() => {
    dispatch(stopSipTrace());
  }, [dispatch]);

  const handleRefresh = useCallback(() => {
    dispatch(getSipTraceMessages());
    dispatch(getSipTraceErrors());
    dispatch(getSipTraceStatus());
  }, [dispatch]);

  const handleViewMessage = useCallback(
    (msgId) => {
      dispatch(getSipTraceMessageDetail(msgId));
      setDetailOpen(true);
    },
    [dispatch]
  );

  const handleViewLadder = useCallback(
    (callId) => {
      setSelectedCallId(callId);
      dispatch(getSipTraceCallMessages(callId));
      setActiveTab(1);
    },
    [dispatch]
  );

  const handleCopyRaw = useCallback(() => {
    if (selectedMessage?.raw) {
      navigator.clipboard.writeText(selectedMessage.raw);
      toast.success("Raw SIP message copied to clipboard");
    }
  }, [selectedMessage]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const totalMessages = parseInt(stats?.total || 0);
  const activeDuration = captureDuration || MAX_CAPTURE_SECONDS_DEFAULT;
  const progressPercent = Math.min((elapsed / activeDuration) * 100, 100);

  return (
    <div className={`App ${colorThem} `}>
      <div className="contant_box">
        <Box
          className="right_sidebox mobile_top_pddng sip-tracer-page"
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
      {/* Header */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h5" fontWeight="bold">
                SIP Tracer
              </Typography>
              <Tooltip title="How it works">
                <IconButton
                  size="small"
                  onClick={() => setInfoOpen(true)}
                  className="sip-tracer-info-btn"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Capture and analyze live SIP traffic from Asterisk
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {/* Status indicator */}
            <Chip
              icon={
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 12,
                    color: capturing ? "#4caf50" : "#9e9e9e",
                    animation: capturing ? "pulse 1.5s infinite" : "none",
                  }}
                />
              }
              label={
                capturing
                  ? `Capturing (${totalMessages} msgs)`
                  : totalMessages > 0
                  ? `Idle (${totalMessages} msgs captured)`
                  : "Idle"
              }
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
            />

            {capturing && (
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                {formatTime(elapsed)} / {formatTime(activeDuration)}
              </Typography>
            )}

            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={handleStart}
              disabled={capturing || actionLoading}
            >
              Start
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<StopIcon />}
              onClick={handleStop}
              disabled={!capturing || actionLoading}
            >
              Stop
            </Button>
            <Tooltip title="Refresh messages from server">
              <IconButton size="small" onClick={handleRefresh} disabled={capturing}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Progress bar during capture */}
        {capturing && (
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ mt: 1, height: 4, borderRadius: 2 }}
          />
        )}
      </Paper>

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="standard"
          className="sip-tracer-tabs"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tab label={`Messages (${messages.length})`} />
          <Tab label="Ladder Diagram" />
          <Tab label={`Errors (${errors.length})`} />
        </Tabs>

      </Paper>

      {/* Tab Content */}
      <Box>
        {activeTab === 0 && (
          <SipTraceMessageGrid
            messages={messages}
            onViewMessage={handleViewMessage}
            onViewLadder={handleViewLadder}
            capturing={capturing}
            isDark={isDark}
          />
        )}
        {activeTab === 1 && (
          <SipTraceLadderDiagram
            messages={selectedCallId ? callMessages : messages}
            selectedCallId={selectedCallId}
            onSelectCallId={(cid) => {
              setSelectedCallId(cid);
              if (cid) dispatch(getSipTraceCallMessages(cid));
            }}
            onViewMessage={handleViewMessage}
            isDark={isDark}
          />
        )}
        {activeTab === 2 && (
          <SipTraceErrorDashboard
            errors={errors}
            onViewMessage={handleViewMessage}
            isDark={isDark}
          />
        )}
      </Box>

      {/* Raw SIP Message Detail Modal */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">SIP Message Detail</Typography>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedMessage && (
            <Box>
              {/* Message metadata */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip
                  label={selectedMessage.direction === "received" ? "RECEIVED" : "SENT"}
                  color={selectedMessage.direction === "received" ? "success" : "primary"}
                  size="small"
                />
                <Chip
                  label={selectedMessage.method || `${selectedMessage.status_code} ${selectedMessage.status_text}`}
                  size="small"
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedMessage.remote_ip}:{selectedMessage.remote_port} ({selectedMessage.transport})
                </Typography>
                {selectedMessage.carrier && (
                  <Chip label={selectedMessage.carrier} size="small" color="info" variant="outlined" />
                )}
                <Typography variant="body2" color="text.secondary">
                  {selectedMessage.bytes} bytes
                </Typography>
              </Stack>

              {/* Raw SIP */}
              <Box
                sx={{
                  bgcolor: "#1e1e1e",
                  color: "#d4d4d4",
                  p: 2,
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  maxHeight: "60vh",
                  overflow: "auto",
                }}
              >
                {selectedMessage.raw || "Raw message not available. Fetch from server."}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopyRaw} size="small" sx={dialogButtonSx}>
            Copy Raw
          </Button>
          <Button onClick={() => setDetailOpen(false)} size="small" sx={dialogButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start Capture Dialog */}
      <Dialog
        open={startDialogOpen}
        onClose={() => setStartDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">Start Capture</Typography>
        </DialogTitle>
        <DialogContent>
          {/* Capture Mode Toggle */}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Capture Mode
          </Typography>
          <ToggleButtonGroup
            value={captureMode}
            exclusive
            onChange={(e, val) => { if (val) setCaptureMode(val); }}
            size="small"
            fullWidth
            sx={{ mb: 2, "& .MuiToggleButton-root": { textTransform: "none" } }}
          >
            <ToggleButton value="quick">
              Quick Capture (60s, no filters)
            </ToggleButton>
            <ToggleButton value="filtered">
              Filtered Capture (with filters)
            </ToggleButton>
          </ToggleButtonGroup>

          {captureMode === "quick" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Captures all SIP traffic for 60 seconds. Best for low-traffic environments or quick diagnostics.
            </Alert>
          )}

          {captureMode === "filtered" && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set at least one filter to narrow the capture. On busy systems (100+ calls), filtering is strongly recommended.
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="DID Number"
                    placeholder="e.g. +18005551234"
                    size="small"
                    fullWidth
                    value={captureFilters.did}
                    onChange={(e) => setCaptureFilters((f) => ({ ...f, did: e.target.value }))}
                  />
                  <TextField
                    label="Caller ID"
                    placeholder="e.g. +14155551234"
                    size="small"
                    fullWidth
                    value={captureFilters.caller_id}
                    onChange={(e) => setCaptureFilters((f) => ({ ...f, caller_id: e.target.value }))}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Carrier"
                    placeholder="e.g. Telnyx"
                    size="small"
                    fullWidth
                    value={captureFilters.carrier}
                    onChange={(e) => setCaptureFilters((f) => ({ ...f, carrier: e.target.value }))}
                  />
                  <TextField
                    label="Remote IP"
                    placeholder="e.g. 192.168.1.100"
                    size="small"
                    fullWidth
                    value={captureFilters.remote_ip}
                    onChange={(e) => setCaptureFilters((f) => ({ ...f, remote_ip: e.target.value }))}
                  />
                </Stack>
                <FormControl size="small" sx={{ maxWidth: 200 }}>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={captureDuration}
                    label="Duration"
                    onChange={(e) => setCaptureDuration(e.target.value)}
                  >
                    <MenuItem value={30}>30 seconds</MenuItem>
                    <MenuItem value={60}>1 minute</MenuItem>
                    <MenuItem value={120}>2 minutes</MenuItem>
                    <MenuItem value={300}>5 minutes</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {!captureFilters.did && !captureFilters.caller_id && !captureFilters.carrier && !captureFilters.remote_ip && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No filters set. On high-traffic systems (100+ concurrent calls), capturing all traffic may impact Asterisk performance and fill the message buffer quickly. Consider setting at least one filter.
                </Alert>
              )}
            </>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: "0.78rem", fontStyle: "italic" }}>
            Capturing enables PJSIP debug logging on Asterisk, which adds disk I/O. Requires at least 2GB free disk space.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDialogOpen(false)} size="small" sx={dialogButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleStartConfirm}
            disabled={actionLoading}
          >
            Start Capture
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoOutlinedIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">SIP Tracer - How It Works</Typography>
          </Stack>
          <IconButton size="small" onClick={() => setInfoOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            The SIP Tracer is a real-time diagnostic tool that captures and parses live SIP (Session Initiation Protocol) signaling
            from your Asterisk PBX. It helps VoIP engineers troubleshoot call setup failures, audio issues, and routing problems.
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            How the Capture Works
          </Typography>
          <Box component="ol" sx={{ pl: 2, "& li": { mb: 1 }, fontSize: "0.875rem" }}>
            <li>When you click <b>Start</b>, a filter dialog appears where you can optionally narrow the capture to a specific <b>DID number</b>, <b>Caller ID</b>, <b>Carrier</b>, or <b>Remote IP</b>. Leave all fields blank to capture all SIP traffic.</li>
            <li>The backend enables PJSIP debug logging on Asterisk via <code>pjsip set logger on</code>.</li>
            <li>A background thread tails the Asterisk log file (<code>/usr/local/asterisk/var/log/asterisk/full.log</code>) in real-time, polling every 100ms.</li>
            <li>Each SIP message boundary (INVITE, BYE, REGISTER, 200 OK, etc.) is detected using regex pattern matching on PJSIP debug output.</li>
            <li>If capture filters are set, only messages matching your filters are kept — non-matching messages are silently discarded. This drastically reduces noise on busy systems with 100s of concurrent calls.</li>
            <li>Matched messages are stored in Redis with a 1-hour TTL and pushed to the browser via WebSocket (Socket.IO) in <b>batched intervals</b> (every 500ms) to keep the UI responsive under high traffic.</li>
            <li>When you click <b>Stop</b> (or after 5 minutes / 5,000 messages), PJSIP debug logging is turned off and the capture threads stop.</li>
          </Box>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Capture Filters
          </Typography>
          <Box component="ul" sx={{ pl: 2, "& li": { mb: 0.5 }, fontSize: "0.875rem" }}>
            <li><b>DID Number</b> - Matches against the From, To, and Request-URI fields. Use this to isolate traffic for a specific DID you are troubleshooting.</li>
            <li><b>Caller ID</b> - Matches against From and To user fields. Use this to trace a specific caller's session.</li>
            <li><b>Carrier</b> - Matches the carrier name (auto-detected from remote IP vs. configured SIP trunks). Use this to isolate traffic from a specific provider.</li>
            <li><b>Remote IP</b> - Exact match on the remote endpoint IP address. Use this to trace all traffic from/to a specific SIP peer.</li>
            <li>Multiple filters can be combined — a message must match <b>all</b> set filters to be captured.</li>
          </Box>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            What Data Is Captured
          </Typography>
          <Box component="ul" sx={{ pl: 2, "& li": { mb: 0.5 }, fontSize: "0.875rem" }}>
            <li><b>Direction</b> - Whether the message was received by or transmitted from Asterisk.</li>
            <li><b>Method / Status</b> - SIP method (INVITE, ACK, BYE, CANCEL, REGISTER, OPTIONS) or response code (100, 180, 200, 403, 503, etc.).</li>
            <li><b>From / To / Call-ID</b> - Parsed from SIP headers to identify call legs and participants.</li>
            <li><b>Remote IP:Port</b> - The carrier or endpoint IP communicating with Asterisk (UDP/TCP/TLS).</li>
            <li><b>Carrier</b> - Auto-detected by matching the remote IP against configured SIP trunks.</li>
            <li><b>Raw SIP Message</b> - Full headers and body preserved for inspection (Authorization headers are redacted for security).</li>
          </Box>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            How to Use Each Tab
          </Typography>
          <Box component="ul" sx={{ pl: 2, "& li": { mb: 0.5 }, fontSize: "0.875rem" }}>
            <li><b>Messages</b> - Searchable grid of all captured SIP messages. Filter by method, phone number, or direction. Click any row to view the raw SIP message.</li>
            <li><b>Ladder Diagram</b> - Visual call flow showing the sequence of SIP messages between Asterisk and remote endpoints. Select a Call-ID to isolate a single call's signaling.</li>
            <li><b>Errors</b> - Dashboard of SIP error responses (4xx client errors, 5xx server errors) with breakdown by error code.</li>
          </Box>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Performance and Data Reliability
          </Typography>
          <Box component="ul" sx={{ pl: 2, "& li": { mb: 0.5 }, fontSize: "0.875rem" }}>
            <li>Messages are parsed directly from Asterisk's own PJSIP debug log — the same data you would see running <code>asterisk -rx "pjsip set logger on"</code> on the CLI.</li>
            <li>Timestamps are taken from the Asterisk log entries, not from the browser, ensuring accurate sequencing.</li>
            <li>Only one capture session can run at a time (mutex-locked) to prevent data corruption.</li>
            <li>Log file rotation is handled automatically — if Asterisk rotates the log during capture, the tracer reopens the new file seamlessly.</li>
            <li>Captured data is stored in Redis and auto-expires after 1 hour. No SIP data is written to disk or persisted in the database.</li>
            <li><b>High-traffic safety:</b> Enabling PJSIP debug logging adds disk I/O on Asterisk. Use capture filters on busy systems (100+ concurrent calls) to minimize the volume of stored messages and keep the UI responsive.</li>
            <li><b>Batched delivery:</b> Messages are buffered on the server and delivered to the browser in batches every 500ms, preventing UI freezes during high-volume captures.</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} size="small" sx={dialogButtonSx}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* CSS animation for pulsing dot */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
      `}</style>
        </Box>
      </div>
    </div>
  );
};

export default SipTracer;
